import {
  getPitchTypeOptions,
  pitchResultLookup,
  playResultLookup,
} from "@aura-frontend/shared";

import type { EditableColumnDef } from "@/components/EditableTable";

import { NOT_A_PITCH } from "./notAPitch";

// Format number to one decimal place or dash if not a number
export const formatToOneDecimal = (value: unknown): string =>
  typeof value === "number" ? value.toFixed(1) : "-";

export type TagRowData = {
  eventUuid: string;
  pitchNumber: number;
  inning?: number;
  half?: "top" | "bottom";
  pitcher?: string;
  pitcherExternalId?: string;
  hitter?: string;
  hitterExternalId?: string;
  pitchType?: string;
  velocity?: number | null;
  spinRate?: number | null;
  exitVelo?: number | null;
  launchAngle?: number | null;
  distance?: number | null;
  pitchResult?: string;
  playResult?: string;
};

export type GameTypeConfig = {
  filters: Array<"inning" | "half" | "pitchType" | "search">;
  columns: EditableColumnDef<TagRowData>[];
  showGrouping: boolean; // Whether to group by inning/half (i.e games)
  needsData?: Array<keyof TagRowData>; // Additional data fields to extract for filtering
};

const PITCH_RESULT_LABELS: Record<string, string> = {
  ...pitchResultLookup,
  [NOT_A_PITCH]: "Ignore",
};

export const RESULT_OPTIONS = [
  { value: "Unknown", label: "Unknown", id: "Unknown" },
  ...Object.entries(PITCH_RESULT_LABELS).map(([key, value]) => ({
    value: key,
    label: value,
    id: key,
  })),
];

export const PLAY_RESULT_OPTIONS = [
  { value: "Unknown", label: "Unknown", id: "Unknown" },
  ...Object.entries(playResultLookup).map(([key, value]) => ({
    value: key,
    label: value,
    id: key,
  })),
];

// Helper to build game type configs with dynamic pitch type options
export const buildGameTypeConfigs = (
  isSoftball: boolean,
): Record<string, GameTypeConfig> => {
  const pitchTypeOptions = [
    { value: "Unknown", label: "Unknown", id: "Unknown" },
    ...getPitchTypeOptions(isSoftball).map((type) => ({
      value: type,
      label: type,
      id: type,
    })),
  ];

  return {
    game: {
      filters: ["inning", "half", "pitchType", "search"],
      showGrouping: true,
      needsData: ["inning", "half", "pitchType"],
      columns: [
        { key: "inning", label: "Inning", editable: false, widthClass: "w-16" },
        {
          key: "pitchNumber",
          label: "Pitch #",
          editable: false,
          widthClass: "w-20",
        },
        {
          key: "hitter",
          label: "Hitter",
          editable: true,
          editor: "searchable-select",
          options: [], // Will be populated with availablePlayers
        },
        {
          key: "pitcher",
          label: "Pitcher",
          editable: true,
          editor: "searchable-select",
          options: [], // Will be populated with availablePlayers
        },
        {
          key: "velocity",
          label: "Velo (mph)",
          editable: false,
          widthClass: "w-16",
          format: formatToOneDecimal,
        },
        {
          key: "pitchType",
          label: "Pitch Type",
          editable: true,
          editor: "select",
          options: pitchTypeOptions,
        },
        {
          key: "exitVelo",
          label: "Exit Velo (mph)",
          editable: false,
          widthClass: "w-24",
          format: formatToOneDecimal,
        },
        {
          key: "pitchResult",
          label: "Pitch Result",
          editable: true,
          editor: "select",
          options: RESULT_OPTIONS,
          format: (value: unknown) =>
            String(PITCH_RESULT_LABELS[String(value)] || value || "-"),
        },
        {
          key: "playResult",
          label: "Play Result",
          editable: true,
          editor: "select",
          options: PLAY_RESULT_OPTIONS,
          format: (value: unknown) =>
            String(playResultLookup[String(value)] || value || "-"),
        },
      ],
    },
    bullpen: {
      filters: ["search"],
      showGrouping: false,
      columns: [
        {
          key: "pitchNumber",
          label: "Pitch #",
          editable: false,
          widthClass: "w-20",
        },
        {
          key: "pitcher",
          label: "Pitcher",
          editable: true,
          editor: "searchable-select",
          options: [], // Will be populated with availablePlayers
        },
        {
          key: "velocity",
          label: "Velocity (mph)",
          editable: false,
          format: formatToOneDecimal,
        },
        {
          key: "pitchType",
          label: "Pitch Type",
          editable: true,
          editor: "select",
          options: pitchTypeOptions,
        },
      ],
    },
    batting_practice: {
      filters: ["search"],
      showGrouping: false,
      columns: [
        {
          key: "pitchNumber",
          label: "Pitch #",
          editable: false,
          widthClass: "w-20",
        },
        {
          key: "hitter",
          label: "Hitter",
          editable: true,
          editor: "searchable-select",
          options: [], // Will be populated with availablePlayers
        },
        {
          key: "exitVelo",
          label: "Exit Velo (mph)",
          editable: false,
          format: formatToOneDecimal,
        },
        {
          key: "launchAngle",
          label: "Launch Angle",
          editable: false,
          format: formatToOneDecimal,
        },
        {
          key: "distance",
          label: "Proj. Distance (ft)",
          editable: false,
          format: formatToOneDecimal,
        },
      ],
    },
  };
};
